import { Atom, atom } from 'jotai';

import { ModalDismissSignal } from './modal-error-boundary';

interface PositionWithSlot {
  slot: number;
}

interface PositionWithId {
  positionId: string;
}

interface SlotOptions<T extends PositionWithSlot> {
  openPositionsAtom: Atom<Record<string, T[]> | null | undefined>;
  symbol: string | null | undefined;
  slot: number | null | undefined;
}

interface PositionIdOptions<T extends PositionWithId> {
  openPositionsAtom: Atom<Record<string, T[]> | null | undefined>;
  symbol: string | null | undefined;
  positionId: string | null | undefined;
}

/**
 * Create a derived atom that returns the open position matching (symbol, slot)
 * or throws ModalDismissSignal. Pair with ModalErrorBoundary so the modal
 * auto-closes when its underlying position is gone.
 */
export function createPositionOrDismissAtom<T extends PositionWithSlot>({
  openPositionsAtom,
  symbol,
  slot,
}: SlotOptions<T>): Atom<T> {
  return atom((get) => {
    if (!symbol || typeof slot !== 'number') {
      throw new ModalDismissSignal('Position not initialized');
    }
    const positions = get(openPositionsAtom);
    const found = positions?.[symbol]?.find((p) => p.slot === slot);
    if (!found) {
      throw new ModalDismissSignal(`Position not found: ${symbol}/${slot}`);
    }
    return found;
  });
}

/**
 * Same as `createPositionOrDismissAtom` but matches by `positionId`.
 */
export function createPositionByIdOrDismissAtom<T extends PositionWithId>({
  openPositionsAtom,
  symbol,
  positionId,
}: PositionIdOptions<T>): Atom<T> {
  return atom((get) => {
    if (!symbol || !positionId) {
      throw new ModalDismissSignal('Position not initialized');
    }
    const positions = get(openPositionsAtom);
    const found = positions?.[symbol]?.find((p) => p.positionId === positionId);
    if (!found) {
      throw new ModalDismissSignal(`Position not found: ${symbol}/${positionId}`);
    }
    return found;
  });
}
